/**
 * BusinessUnitSelector - TopBar dropdown that scopes the entire app to
 * a single Business Unit, OR "All Business Units" (default).
 *
 * Reads/writes the shared `vaultconsent-ui` localStorage key via the
 * Zustand ui store (see `stores/ui.ts`). Same key + same shape as Core,
 * so a BU pick in either app propagates to the other automatically
 * when both run at the same origin (`<slug>.foctta.com`).
 *
 * Edge-case rendering:
 *   - 0 BUs available  → component hides itself (returns null).
 *   - 1 BU available   → static label, no dropdown (selection implicit).
 *   - 2+ BUs available → dropdown with "All Business Units" + each BU.
 *
 * The user's BU scope from the JWT (`bua`/`bus` claims) is read by the
 * server-side RBAM layer; the client just renders whatever the API
 * returns. If a user without `bua=true` only has access to 2 BUs, the
 * server's listBusinessUnits filters accordingly and we only show those.
 */

import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2, Check, ChevronDown } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/primitives';
import { Button } from '@/components/ui/button';
import { listBusinessUnits, type BusinessUnit } from '@/lib/api';
import { useUiStore } from '@/stores/ui';

export function BusinessUnitSelector(): JSX.Element | null {
  const { selectedBusinessUnitId, selectedBusinessUnitName, setBusinessUnit } = useUiStore();

  const { data } = useQuery({
    queryKey: ['business-units', 'active'],
    queryFn: () => listBusinessUnits({ status: 'active' }),
    staleTime: 5 * 60_000, // 5 min - BUs change rarely
  });

  const units: BusinessUnit[] = useMemo(() => data?.businessUnits ?? [], [data?.businessUnits]);

  // If the persisted selection refers to a BU that no longer exists
  // (renamed slug, archived, or this is a fresh tenant), clear it so
  // the dropdown reverts to "All Business Units" instead of showing a
  // ghost selection.
  useEffect(() => {
    if (!selectedBusinessUnitId) return;
    if (units.length === 0) return; // still loading or genuinely empty
    const stillExists = units.some((u) => u.id === selectedBusinessUnitId);
    if (!stillExists) {
      setBusinessUnit(null, null);
    }
  }, [selectedBusinessUnitId, units, setBusinessUnit]);

  // ── Render variants ────────────────────────────────────────────────

  // 0 BUs → hide entirely. The tenant doesn't use BUs; the
  // industry-pack resolver falls back to tenant-level packs.
  if (units.length === 0) return null;

  // 1 BU → static label. No dropdown to confuse the user with.
  if (units.length === 1) {
    const only = units[0]!;
    // Sync the store if it's not already pointing at the only BU.
    if (selectedBusinessUnitId !== only.id) {
      setBusinessUnit(only.id, only.name);
    }
    return (
      <div className="hidden md:flex items-center gap-1.5 px-2 py-1 rounded-md bg-card/30 border border-border/40 text-xs">
        <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="font-medium truncate max-w-[180px]">{only.name}</span>
      </div>
    );
  }

  // 2+ BUs → real dropdown.
  const label = selectedBusinessUnitName ?? 'All Business Units';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 px-2 text-xs font-medium glass-lite border border-border/40 hover:border-border/70"
        >
          <Building2 className="h-3.5 w-3.5" />
          <span className="truncate max-w-[140px]">{label}</span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuItem
          onClick={() => setBusinessUnit(null, null)}
          className="flex items-center justify-between gap-2"
        >
          <div>
            <div className="text-xs font-medium">All Business Units</div>
            <div className="text-[10px] text-muted-foreground">
              Tenant-wide view; uses tenant-level industry packs
            </div>
          </div>
          {!selectedBusinessUnitId && <Check className="h-4 w-4 shrink-0" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {units.map((u) => (
          <DropdownMenuItem
            key={u.id}
            onClick={() => setBusinessUnit(u.id, u.name)}
            className="flex items-center justify-between gap-2"
          >
            <div className="min-w-0">
              <div className="text-xs font-medium truncate">{u.name}</div>
              <div className="text-[10px] text-muted-foreground truncate">
                {u.source === 'foctta_core' ? 'synced from FOCTTA Core' : 'local'}
                {u.description ? ` · ${u.description}` : ''}
              </div>
            </div>
            {selectedBusinessUnitId === u.id && <Check className="h-4 w-4 shrink-0" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
